/**
 * Integrations API module (ServiceNow, Slack, Jira, Ticket Sync, Integration Hub).
 *
 * Real per-domain split from services/api.ts. Verbatim extraction of the 5
 * integration API objects; behavior is unchanged. Other integration-adjacent
 * exports (siemApi, dataLakeApi, etc.) remain in api.ts and are re-exported
 * by services/api/index.ts.
 */
import { _unwrap, _thenUnwrap } from './_unwrap';
// `apiClient` is a NAMED export from ./client (verified against vulnerability/ai/easm
// sibling splits). The earlier default-import form would fail at runtime.
import { apiClient as fetchClient } from './client';

// ── ServiceNow Integration API (Gap G) ───────────────────────────────────────
// R161 (2026-05-18): aligned FE paths with actual BE routes after R151 audit.
// Pre-R161: FE used `/config` + `/incidents`; BE actually exposes `/configure`
// + `/create-incident`. Pre-R161 the 4 wrappers had 0 consumers (no UI) but
// would have produced 404s if wired. Now paths match notification-service +
// vulnerability-management BE routes.
//   BE: POST /api/v1/integrations/servicenow/configure       (configure)
//   BE: POST /api/v1/integrations/servicenow/create-incident (createIncident)
//   BE: POST /api/v1/integrations/servicenow/sync            (syncFindings)
//   BE: GET  /api/v1/integrations/servicenow/sync-status     (getSyncStatus, NEW)
export const serviceNowApi = {
  // Configuration management. Pre-R161 FE used "/config" — was a 404.
  getConfig: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/integrations/servicenow/configure'));
  },
  saveConfig: async (config: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/integrations/servicenow/configure', config));
  },
  // Pre-R161 FE used "/incidents" — was a 404. Real path is "/create-incident".
  createIncident: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/integrations/servicenow/create-incident', data));
  },
  syncFindings: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/integrations/servicenow/sync'));
  },
  // R161 new: surface inbound sync status (BE existed, no wrapper)
  getSyncStatus: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/integrations/servicenow/sync-status'));
  },
};

// ── Slack Bot API (Gap H) ────────────────────────────────────────────────────
// R161 (2026-05-18): aligned FE paths with actual BE routes.
// Pre-R161: FE used `/config`; BE exposes `/configure`. Same path-mismatch
// as serviceNowApi above. 0 consumers pre-fix.
export const slackBotApi = {
  getConfig: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/integrations/slack/configure'));
  },
  saveConfig: async (config: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/integrations/slack/configure', config));
  },
};

// ── Bidirectional Sync API ──
export const ticketSyncApi = {
  createConfig: (data: any) => fetchClient.post('/api/v1/integrations/sync/configs', data).then(_thenUnwrap),
  listConfigs: () => fetchClient.get('/api/v1/integrations/sync/configs').then(_thenUnwrap),
  updateConfig: (id: string, data: any) => fetchClient.put(`/api/v1/integrations/sync/configs/${id}`, data).then(_thenUnwrap),
  deleteConfig: (id: string) => fetchClient.delete(`/api/v1/integrations/sync/configs/${id}`).then(_thenUnwrap),
  triggerSync: (id: string) => fetchClient.post(`/api/v1/integrations/sync/trigger/${id}`).then(_thenUnwrap),
  getStatus: (id: string) => fetchClient.get(`/api/v1/integrations/sync/status/${id}`).then(_thenUnwrap),
  getHistory: (id: string) => fetchClient.get(`/api/v1/integrations/sync/history/${id}`).then(_thenUnwrap),
  linkTicket: (data: { ticket_key: string; finding_id: string }) => fetchClient.post('/api/v1/integrations/sync/link', data).then(_thenUnwrap),
  listLinks: () => fetchClient.get('/api/v1/integrations/sync/links').then(_thenUnwrap),
  unlinkTicket: (ticketKey: string) => fetchClient.delete(`/api/v1/integrations/sync/links/${ticketKey}`).then(_thenUnwrap),
};

// ============================================================================
// Jira Bi-directional Sync
// ============================================================================
//
// R161 (2026-05-18): post-audit reconciliation.
// BE actual surface (vulnerability-management + notification-service):
//   POST /api/v1/integrations/jira/sync            (manual outbound trigger)
//   GET  /api/v1/integrations/jira/projects        (list projects)
//   GET  /api/v1/integrations/jira/sync-status     (inbound sync status)
//
// Pre-R161 the FE wrapper assumed a richer surface (`/sync/trigger`,
// `/sync/history`, `/sync/mappings`, `/sync/conflicts`, `/sync/conflicts/{id}/resolve`)
// that BE never implemented. All 7 methods had 0 consumers, so no UI broke,
// but anything wiring them would 404. R161 keeps the wrapper but:
//   - aligns paths that DO have BE (getSyncStatus → `/sync-status`)
//   - marks the aspirational methods with a clear "// ASPIRATIONAL" comment
//     so future readers know they're unimplemented BE-side.
//
// To consume aspirational methods: build the BE handler first.
export const jiraSyncApi = {
  // R161: aligned to existing BE route `/sync-status` (was `/sync/status` → 404)
  getSyncStatus: async (): Promise<any> =>
    _unwrap(await fetchClient.get<any>('/api/v1/integrations/jira/sync-status')),
  // R161: existing BE — POST /api/v1/integrations/jira/sync (no body needed, kept for back-compat)
  triggerSync: async (data?: { direction?: 'inbound' | 'outbound' | 'both'; project_key?: string }): Promise<any> =>
    _unwrap(await fetchClient.post<any>('/api/v1/integrations/jira/sync', data)),
  // R161: NEW wrapper — projects listing (BE existed, no wrapper)
  getProjects: async (): Promise<any> =>
    _unwrap(await fetchClient.get<any>('/api/v1/integrations/jira/projects')),
  // NOTE: the previously-defined sync history / field-mapping / conflict
  // wrappers (getSyncHistory, getMappings, setMappings, getConflicts,
  // resolveConflict) were removed — they targeted BE handlers that were never
  // implemented (guaranteed 404) and had zero consumers. Re-add them only
  // alongside the corresponding BE routes so the API surface stays honest.
};

// ============================================================================
// Integration Hub API
// ============================================================================

// Migrated to canonical fetch client (services/api/client.ts).
//
// P0 wiring fix (2026-07-02): the hub previously POSTed configure/test/
// disconnect to `/api/v1/integrations/configure/{id}` — a route the backend
// NEVER exposed (guaranteed 404, so the 3 buttons were dead). The real
// notification-service surface is PER-PROVIDER configure plus generic per-id
// test / delete:
//   POST   /api/v1/integrations/{jira,servicenow,slack}/configure
//   POST   /api/v1/integrations/{integration_id}/test
//   DELETE /api/v1/integrations/{integration_id}
// getStats hits `/api/v1/integrations/stats` which the gateway rewrites to
// `/api/v1/integrations/stats/overview` (returns `total_integrations` + health).
export const integrationHubApi = {
  getCatalog: async (params?: any): Promise<any> =>
    _unwrap(await fetchClient.get<any>('/api/v1/integrations/catalog', params as any)),
  getDetail: async (id: string): Promise<any> =>
    _unwrap(await fetchClient.get<any>(`/api/v1/integrations/catalog/${id}`)),
  getStats: async (): Promise<any> =>
    _unwrap(await fetchClient.get<any>('/api/v1/integrations/stats')),
  // Real per-provider configure — only jira / servicenow / slack have BE routes.
  configureProvider: async (provider: string, data: any): Promise<any> =>
    _unwrap(await fetchClient.post<any>(`/api/v1/integrations/${provider}/configure`, data)),
  // Generic connectivity test for an already-configured integration.
  testConnection: async (id: string): Promise<any> =>
    _unwrap(await fetchClient.post<any>(`/api/v1/integrations/${id}/test`, {})),
  // Remove/disconnect a configured integration.
  disconnect: async (id: string): Promise<any> =>
    _unwrap(await fetchClient.delete<any>(`/api/v1/integrations/${id}`)),
};
